import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import axios from "axios";
import swal from "sweetalert";
import { Modal } from "react-bootstrap";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const PER_PAGE = 6;


/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {

    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
        return "0";
    }

    return new Intl.NumberFormat("en-BD", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(number);
};


const money = (value) => {
    return `৳${numberFormat(value)}`;
};


const safeText = (
    value,
    fallback = "—"
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    return value;
};


const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const date = new Date(
            String(value).replace(
                " ",
                "T"
            )
        );

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleString(
            "en-BD",
            {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }
        );

    } catch (error) {

        return value;
    }
};


/* =========================================================
   NORMALIZE API LOG

   IMPORTANT:

   price          = TOTAL TARGET
   booking_amount = TOTAL PAID

========================================================= */

const normalizeLog = (item) => {

    /*
    |--------------------------------------------------------------------------
    | TOTAL TARGET
    |--------------------------------------------------------------------------
    */

    const target = Number(
        item?.target_amount ??
        item?.share_price ??
        item?.price ??
        0
    );


    /*
    |--------------------------------------------------------------------------
    | TOTAL PAID
    |--------------------------------------------------------------------------
    */

    const paid = Number(
        item?.paid_amount ??
        item?.fixt_amount ??
        0
    );


    /*
    |--------------------------------------------------------------------------
    | REMAINING
    |--------------------------------------------------------------------------
    */

    const remaining = Math.max(
        0,
        target - paid
    );


    /*
    |--------------------------------------------------------------------------
    | PROGRESS
    |--------------------------------------------------------------------------
    */

    let progress = 0;

    if (target > 0) {

        progress =
            (paid / target) * 100;
    }

    progress = Math.min(
        100,
        Math.max(
            0,
            progress
        )
    );


    /*
    |--------------------------------------------------------------------------
    | COMPLETE
    |--------------------------------------------------------------------------
    */

    const completed =
        target > 0 &&
        paid >= target;


    return {

        ...item,

        target_amount:
            target,

        paid_amount:
            paid,

        remaining_amount:
            remaining,

        progress_percentage:
            progress,

        completed:
            completed,

        payment_status:
            completed
                ? "Completed"
                : "Installment Running"
    };
};


/* =========================================================
   COMPONENT
========================================================= */

const BasicDatatable = () => {


    /* =====================================================
       AUTH
    ===================================================== */

    const authToken =
        localStorage.getItem(
            "auth_token"
        );

    const selectedCurrency =
        localStorage.getItem(
            "selectedCurrency"
        );


    const currency =
        selectedCurrency
            ? selectedCurrency.replace(
                /"/g,
                ""
            )
            : "BDT";


    /* =====================================================
       STATE
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [
        currentPage,
        setCurrentPage
    ] = useState(1);


    /* =====================================================
       PAYMENT MODAL
    ===================================================== */

    const [
        showPaymentModal,
        setShowPaymentModal
    ] = useState(false);

    const [
        selectedLog,
        setSelectedLog
    ] = useState(null);

    const [
        paymentAmount,
        setPaymentAmount
    ] = useState("");

    const [
        password,
        setPassword
    ] = useState("");

    const [
        showPassword,
        setShowPassword
    ] = useState(false);

    const [
        paymentLoading,
        setPaymentLoading
    ] = useState(false);


    /* =====================================================
       HISTORY MODAL
    ===================================================== */

    const [
        showHistoryModal,
        setShowHistoryModal
    ] = useState(false);

    const [
        historyLog,
        setHistoryLog
    ] = useState(null);

    const [
        history,
        setHistory
    ] = useState([]);

    const [
        historyLoading,
        setHistoryLoading
    ] = useState(false);


    /* =====================================================
       FETCH DATA
    ===================================================== */

    const fetchData = async () => {

        if (!authToken) {

            setLoading(false);

            setPageError(
                "Your login session was not found. Please login again."
            );

            return;
        }


        try {

            setLoading(true);

            setPageError("");


            const response =
                await axios.post(
                    "/apps-migration-log",
                    {
                        key_s:
                            API_KEY,

                        currency:
                            currency
                    }
                );


            if (
                response?.data?.status ===
                "success"
            ) {

                const apiRows =
                    Array.isArray(
                        response?.data?.data
                    )
                        ? response.data.data
                        : [];


                /*
                |--------------------------------------------------------------------------
                | NORMALIZE DATA
                |--------------------------------------------------------------------------
                */

                const rows =
                    apiRows.map(
                        (item) =>
                            normalizeLog(
                                item
                            )
                    );


                setIncome(rows);

            } else {

                setIncome([]);

                setPageError(
                    response?.data?.message ||
                    "Unable to load investment logs."
                );
            }

        } catch (error) {

            console.error(
                "Investment log error:",
                error
            );

            setIncome([]);

            setPageError(
                error?.response?.data
                    ?.message ||
                "Unable to load investment logs."
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        fetchData();

        /*
         * Page open করলে top
         */

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });

    }, []);


    /* =====================================================
       SEARCH
    ===================================================== */

    const filteredLogs =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();

            if (!keyword) {

                return income;
            }


            return income.filter(
                (item) => {

                    const searchable = [

                        item.project_name,

                        item.packege_name,

                        item.project_type,

                        item.type,

                        item.payment_status,

                        item.pack_type,

                        item.id,

                        item.draft_rand

                    ]
                        .join(" ")
                        .toLowerCase();


                    return searchable.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredLogs.length /
                PER_PAGE
            )
        );


    useEffect(() => {

        if (
            currentPage >
            totalPages
        ) {

            setCurrentPage(
                totalPages
            );
        }

    }, [
        currentPage,
        totalPages
    ]);


    const currentLogs =
        useMemo(() => {

            const start =
                (currentPage - 1) *
                PER_PAGE;


            return filteredLogs.slice(
                start,
                start + PER_PAGE
            );

        }, [
            filteredLogs,
            currentPage
        ]);


    /*
    |--------------------------------------------------------------------------
    | CHANGE PAGE + GO TOP
    |--------------------------------------------------------------------------
    */

    const changePage = (page) => {

        const finalPage =
            Math.max(
                1,
                Math.min(
                    page,
                    totalPages
                )
            );


        setCurrentPage(
            finalPage
        );


        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(() => {

            return income.reduce(
                (
                    result,
                    item
                ) => {

                    const target =
                        Number(
                            item.target_amount ||
                            0
                        );

                    const paid =
                        Number(
                            item.paid_amount ||
                            0
                        );

                    const remaining =
                        Number(
                            item.remaining_amount ||
                            0
                        );


                    result.target +=
                        target;

                    result.paid +=
                        paid;

                    result.remaining +=
                        remaining;


                    if (
                        target > 0 &&
                        paid >= target
                    ) {

                        result.completed +=
                            1;
                    }


                    return result;
                },
                {
                    target: 0,
                    paid: 0,
                    remaining: 0,
                    completed: 0
                }
            );

        }, [income]);


    /* =====================================================
       OPEN PAYMENT
    ===================================================== */

    const openPaymentModal = (
        log
    ) => {

        const item =
            normalizeLog(log);


        const remaining =
            Number(
                item.remaining_amount ||
                0
            );


        if (
            item.completed ||
            remaining <= 0
        ) {

            swal(
                "Investment Completed",
                "The full target amount has already been paid.",
                "success"
            );

            return;
        }


        setSelectedLog(
            item
        );

        setPaymentAmount("");

        setPassword("");

        setShowPassword(false);

        setShowPaymentModal(
            true
        );
    };


    const closePaymentModal = () => {

        if (paymentLoading) {
            return;
        }

        setShowPaymentModal(
            false
        );

        setSelectedLog(null);

        setPaymentAmount("");

        setPassword("");

        setShowPassword(false);
    };


    /* =====================================================
       PAYMENT AMOUNT
    ===================================================== */

    const handleAmountChange = (
        event
    ) => {

        let value =
            event.target.value;


        value =
            value.replace(
                /[^0-9.]/g,
                ""
            );


        const parts =
            value.split(".");


        if (
            parts.length > 2
        ) {

            value =
                parts[0] +
                "." +
                parts
                    .slice(1)
                    .join("");
        }


        setPaymentAmount(
            value
        );
    };


    /* =====================================================
       QUICK PAYMENT
    ===================================================== */

    const setQuickPayment = (
        amount
    ) => {

        if (!selectedLog) {
            return;
        }


        const remaining =
            Number(
                selectedLog
                    .remaining_amount ||
                0
            );


        const requested =
            Number(
                amount || 0
            );


        const finalAmount =
            Math.min(
                requested,
                remaining
            );


        if (
            finalAmount > 0
        ) {

            setPaymentAmount(
                String(
                    finalAmount
                )
            );
        }
    };


    /* =====================================================
       SUBMIT INSTALLMENT
    ===================================================== */

    const submitInstallment =
        async (event) => {

            event.preventDefault();


            if (
                !selectedLog ||
                paymentLoading
            ) {

                return;
            }


            const amount =
                Number(
                    paymentAmount
                );


            const remaining =
                Number(
                    selectedLog
                        .remaining_amount ||
                    0
                );


            /*
            |--------------------------------------------------------------------------
            | VALIDATION
            |--------------------------------------------------------------------------
            */

            if (
                !amount ||
                amount <= 0
            ) {

                swal(
                    "Amount Required",
                    "Please enter a valid installment amount.",
                    "warning"
                );

                return;
            }


            if (
                amount > remaining
            ) {

                swal(
                    "Amount Too High",
                    `Maximum payable amount is ${money(
                        remaining
                    )}.`,
                    "warning"
                );

                return;
            }


            if (
                !password.trim()
            ) {

                swal(
                    "Password Required",
                    "Please enter your account password.",
                    "warning"
                );

                return;
            }


            try {

                setPaymentLoading(
                    true
                );


                const response =
                    await axios.post(
                        "/investment-installment",
                        {
                            key_s:
                                API_KEY,

                            log_id:
                                selectedLog.id ||
                                selectedLog
                                    .draft_rand,

                            amount:
                                amount,

                            password:
                                password,

                            currency:
                                currency
                        }
                    );


                const result =
                    response?.data || {};


                if (
                    result.status ===
                    "success"
                ) {

                    /*
                     * Close manually
                     * Don't use closePaymentModal()
                     * because loading = true.
                     */

                    setShowPaymentModal(
                        false
                    );

                    setSelectedLog(
                        null
                    );

                    setPaymentAmount("");

                    setPassword("");


                    await swal(
                        "Payment Successful",
                        result.message ||
                        "Installment payment completed successfully.",
                        "success"
                    );


                    /*
                     * Reload values from backend
                     */

                    await fetchData();


                } else {

                    swal(
                        "Unable to Pay",
                        result.message ||
                        "Installment payment could not be completed.",
                        "warning"
                    );
                }


            } catch (error) {

                console.error(
                    "Installment error:",
                    error
                );


                swal(
                    "Payment Failed",
                    error?.response?.data
                        ?.message ||
                    "Unable to process installment payment.",
                    "error"
                );


            } finally {

                setPaymentLoading(
                    false
                );
            }
        };


    const closeHistoryModal = () => {

        setShowHistoryModal(
            false
        );

        setHistoryLog(null);

        setHistory([]);
    };


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>

            <style>{`

                * {
                    box-sizing: border-box;
                }

                .investment-log-page {

                    --primary: #5d50f5;
                    --primary-dark: #493be7;
                    --primary-soft: #f2f0ff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --orange: #f79009;
                    --orange-dark: #b54708;
                    --orange-soft: #fff8e8;

                    --red: #f04438;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --background: #f6f7fb;

                    width: 100%;
                    min-height: 100vh;

                    padding: 25px;

                    background: #f6f7fb;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .investment-log-container {

                    width: 100%;
                    max-width: 1450px;

                    margin: 0 auto;
                }


                /* ==============================
                   TOP
                ============================== */

                .page-heading {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 22px;
                }


                .page-heading h2 {

                    margin: 0 0 5px;

                    color: var(--text);

                    font-size: 28px;

                    font-weight: 800;
                }


                .page-heading p {

                    margin: 0;

                    color: var(--muted);

                    font-size: 15px;
                }


                .page-record-count {

                    padding: 11px 17px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 12px;

                    background: #fff;

                    color: var(--text2);

                    font-size: 14px;

                    font-weight: 700;
                }


                /* ==============================
                   SUMMARY
                ============================== */

                .log-summary-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(4, 1fr);

                    gap: 15px;

                    margin-bottom: 20px;
                }


                .log-summary-card {

                    padding: 20px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 16px;

                    background: #fff;

                    box-shadow:
                        0 5px 20px
                        rgba(16,24,40,.04);
                }


                .summary-title {

                    display: block;

                    margin-bottom: 8px;

                    color: var(--muted);

                    font-size: 14px;

                    font-weight: 600;
                }


                .log-summary-card strong {

                    display: block;

                    color: var(--text);

                    font-size: 24px;

                    line-height: 1.2;

                    font-weight: 800;
                }


                .summary-paid strong {
                    color: var(--green-dark);
                }


                .summary-remaining strong {
                    color: var(--orange-dark);
                }


                .summary-completed strong {
                    color: var(--primary);
                }


                /* ==============================
                   TOOLBAR
                ============================== */

                .log-toolbar {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding: 17px;

                    margin-bottom: 18px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 16px;

                    background: #fff;
                }


                .log-toolbar-title strong {

                    display: block;

                    color: var(--text);

                    font-size: 18px;

                    font-weight: 800;
                }


                .log-toolbar-title span {

                    display: block;

                    margin-top: 3px;

                    color: var(--muted);

                    font-size: 13px;
                }


                .log-search {

                    position: relative;

                    width: 330px;

                    max-width: 100%;
                }


                .log-search input {

                    width: 100%;
                    height: 48px;

                    padding:
                        0 15px 0 42px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 12px;

                    outline: none;

                    background: #f9fafb;

                    color: var(--text);

                    font-size: 14px;
                }


                .log-search input:focus {

                    border-color:
                        var(--primary);

                    background: #fff;

                    box-shadow:
                        0 0 0 4px
                        rgba(93,80,245,.08);
                }


                .log-search-icon {

                    position: absolute;

                    top: 50%;
                    left: 15px;

                    transform:
                        translateY(-50%);

                    color: var(--muted);

                    font-size: 18px;
                }


                /* ==============================
                   CARDS
                ============================== */

                .investment-card-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0,1fr)
                        );

                    gap: 18px;
                }


                .investment-log-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 20px;

                    background: #fff;

                    box-shadow:
                        0 6px 25px
                        rgba(16,24,40,.05);
                }


                .investment-card-header {

                    display: flex;

                    align-items: flex-start;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding: 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .investment-project-info {

                    display: flex;

                    align-items: center;

                    gap: 13px;

                    min-width: 0;
                }


                .investment-project-icon {

                    flex: 0 0 48px;

                    width: 48px;
                    height: 48px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 14px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;

                    font-weight: 900;
                }


                .investment-project-name {

                    margin-bottom: 4px;

                    color: var(--text);

                    font-size: 18px;

                    line-height: 1.35;

                    font-weight: 800;
                }


                .investment-project-type {

                    color: var(--muted);

                    font-size: 13px;

                    font-weight: 500;
                }


                .payment-status {

                    flex-shrink: 0;

                    padding:
                        7px 11px;

                    border-radius: 50px;

                    background:
                        var(--orange-soft);

                    color:
                        var(--orange-dark);

                    font-size: 11px;

                    font-weight: 800;

                    text-transform:
                        uppercase;
                }


                .payment-status.completed {

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);
                }


                .investment-card-body {

                    padding: 20px;
                }


                /* ==============================
                   AMOUNTS
                ============================== */

                .investment-amount-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(3, 1fr);

                    gap: 11px;

                    margin-bottom: 18px;
                }


                .investment-amount-box {

                    min-height: 82px;

                    padding: 14px;

                    border:
                        1px solid
                        #e8ebf0;

                    border-radius: 14px;

                    background: #fafbfc;
                }


                .investment-amount-box span {

                    display: block;

                    margin-bottom: 7px;

                    color: var(--muted);

                    font-size: 13px;

                    font-weight: 600;
                }


                .investment-amount-box strong {

                    display: block;

                    color: var(--text);

                    font-size: 19px;

                    line-height: 1.25;

                    font-weight: 800;
                }


                .investment-amount-box.paid {

                    border-color:
                        #abefc6;

                    background:
                        var(--green-soft);
                }


                .investment-amount-box.paid strong {

                    color:
                        var(--green-dark);
                }


                .investment-amount-box.remaining {

                    border-color:
                        #fedf89;

                    background:
                        var(--orange-soft);
                }


                .investment-amount-box.remaining strong {

                    color:
                        var(--orange-dark);
                }


                /* ==============================
                   PROGRESS
                ============================== */

                .progress-section {

                    padding: 17px;

                    margin-bottom: 18px;

                    border:
                        1px solid
                        #eceef3;

                    border-radius: 15px;

                    background: #f8f9fc;
                }


                .progress-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 11px;
                }


                .progress-header span {

                    color: var(--text2);

                    font-size: 14px;

                    font-weight: 700;
                }


                .progress-header strong {

                    color: var(--primary);

                    font-size: 16px;

                    font-weight: 800;
                }


                .custom-progress {

                    overflow: hidden;

                    width: 100%;
                    height: 13px;

                    border-radius: 50px;

                    background: #e5e7eb;
                }


                .custom-progress-bar {

                    height: 100%;

                    border-radius: 50px;

                    background:
                        linear-gradient(
                            90deg,
                            #5d50f5,
                            #8b82ff
                        );

                    transition:
                        width .5s ease;
                }


                .custom-progress-bar.complete {

                    background:
                        linear-gradient(
                            90deg,
                            #12b76a,
                            #47cd89
                        );
                }


                .progress-numbers {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-top: 10px;

                    color: var(--muted);

                    font-size: 12px;

                    font-weight: 600;
                }


                /* ==============================
                   DETAILS
                ============================== */

                .investment-detail-row {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding: 11px 0;

                    border-bottom:
                        1px dashed
                        #e4e7ec;
                }


                .investment-detail-row span {

                    color: var(--muted);

                    font-size: 14px;
                }


                .investment-detail-row strong {

                    color: var(--text2);

                    font-size: 14px;

                    font-weight: 700;

                    text-align: right;
                }


                /* ==============================
                   BUTTON
                ============================== */

                .investment-card-actions {

                    display: grid;

                    gap: 11px;

                    margin-top: 19px;
                }


                .history-button,
                .installment-button {

                    min-height: 49px;

                    padding:
                        10px 15px;

                    border-radius: 12px;

                    font-size: 14px;

                    font-weight: 800;

                    cursor: pointer;

                    transition:
                        .2s ease;
                }


                .history-button {

                    border:
                        1px solid
                        var(--border);

                    background: #fff;

                    color: var(--text2);
                }


                .history-button:hover {

                    border-color:
                        var(--primary);

                    color:
                        var(--primary);

                    background:
                        var(--primary-soft);
                }


                .installment-button {

                    border: 0;

                    background:
                        linear-gradient(
                            135deg,
                            var(--primary),
                            var(--primary-dark)
                        );

                    color: #fff;

                    box-shadow:
                        0 7px 18px
                        rgba(93,80,245,.20);
                }


                .installment-button:hover {

                    transform:
                        translateY(-1px);
                }


                .installment-button:disabled {

                    background: #e4e7ec;

                    color: #98a2b3;

                    box-shadow: none;

                    cursor: not-allowed;

                    transform: none;
                }


                /* ==============================
                   EMPTY
                ============================== */

                .log-status-box {

                    grid-column:
                        1 / -1;

                    padding:
                        60px 20px;

                    border:
                        1px dashed
                        #d0d5dd;

                    border-radius: 18px;

                    background: #fff;

                    text-align: center;
                }


                .log-status-box strong {

                    display: block;

                    margin-bottom: 7px;

                    color: var(--text);

                    font-size: 18px;
                }


                .log-status-box span {

                    color: var(--muted);

                    font-size: 14px;
                }


                .log-status-icon {

                    width: 56px;
                    height: 56px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin:
                        0 auto 14px;

                    border-radius: 15px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 24px;

                    font-weight: 900;
                }


                .investment-skeleton {

                    height: 430px;

                    border-radius: 20px;

                    background:
                        linear-gradient(
                            90deg,
                            #eee 25%,
                            #f8f8f8 37%,
                            #eee 63%
                        );

                    background-size:
                        400% 100%;

                    animation:
                        loadingAnimation
                        1.4s ease infinite;
                }


                @keyframes loadingAnimation {

                    0% {
                        background-position:
                            100% 0;
                    }

                    100% {
                        background-position:
                            -100% 0;
                    }
                }


                /* ==============================
                   PAGINATION
                ============================== */

                .log-pagination {

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-wrap: wrap;

                    gap: 8px;

                    margin-top: 25px;
                }


                .log-page-button {

                    min-width: 42px;
                    height: 42px;

                    padding:
                        0 12px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    background: #fff;

                    color: var(--text2);

                    font-size: 14px;

                    font-weight: 800;

                    cursor: pointer;
                }


                .log-page-button.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color: #fff;
                }


                .log-page-button:disabled {

                    opacity: .45;

                    cursor: not-allowed;
                }


                /* ==============================
                   MODAL
                ============================== */

                .installment-modal
                .modal-content {

                    overflow: hidden;

                    border: 0;

                    border-radius: 20px;

                    box-shadow:
                        0 25px 70px
                        rgba(16,24,40,.20);
                }


                .installment-modal-header {

                    position: relative;

                    padding: 24px;

                    background:
                        linear-gradient(
                            135deg,
                            #171c2e,
                            #383369
                        );

                    color: #fff;
                }


                .installment-modal-header h4 {

                    margin:
                        0 0 5px;

                    color: #fff;

                    font-size: 22px;

                    font-weight: 800;
                }


                .installment-modal-header p {

                    margin: 0;

                    padding-right: 45px;

                    color:
                        rgba(255,255,255,.70);

                    font-size: 13px;
                }


                .modal-close-button {

                    position: absolute;

                    top: 18px;
                    right: 18px;

                    width: 36px;
                    height: 36px;

                    border:
                        1px solid
                        rgba(255,255,255,.15);

                    border-radius: 10px;

                    background:
                        rgba(255,255,255,.08);

                    color: #fff;

                    font-size: 21px;

                    cursor: pointer;
                }


                .installment-modal-body {

                    padding: 23px;
                }


                .modal-project-box {

                    padding: 15px;

                    margin-bottom: 17px;

                    border:
                        1px solid
                        #dedaff;

                    border-radius: 13px;

                    background: #f8f7ff;
                }


                .modal-project-box small {

                    display: block;

                    margin-bottom: 4px;

                    color: var(--muted);

                    font-size: 11px;

                    font-weight: 700;

                    text-transform:
                        uppercase;
                }


                .modal-project-box strong {

                    display: block;

                    color: var(--text);

                    font-size: 16px;

                    font-weight: 800;
                }


                .modal-amount-summary {

                    display: grid;

                    grid-template-columns:
                        repeat(3,1fr);

                    gap: 9px;

                    margin-bottom: 20px;
                }


                .modal-amount-summary div {

                    padding: 12px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    background: #fafbfc;
                }


                .modal-amount-summary span {

                    display: block;

                    margin-bottom: 4px;

                    color: var(--muted);

                    font-size: 11px;
                }


                .modal-amount-summary strong {

                    display: block;

                    color: var(--text);

                    font-size: 14px;

                    font-weight: 800;
                }


                .modal-field {

                    margin-bottom: 17px;
                }


                .modal-field label {

                    display: block;

                    margin-bottom: 8px;

                    color: var(--text2);

                    font-size: 14px;

                    font-weight: 700;
                }


                .modal-money-input,
                .modal-password-input {

                    position: relative;
                }


                .modal-money-input span {

                    position: absolute;

                    top: 50%;
                    left: 15px;

                    transform:
                        translateY(-50%);

                    color: var(--primary);

                    font-size: 20px;

                    font-weight: 800;
                }


                .modal-money-input input,
                .modal-password-input input {

                    width: 100%;
                    height: 53px;

                    border:
                        1.5px solid
                        #d0d5dd;

                    border-radius: 12px;

                    outline: none;

                    background: #fff;

                    color: var(--text);

                    font-size: 15px;

                    font-weight: 600;
                }


                .modal-money-input input {

                    padding:
                        0 15px 0 40px;
                }


                .modal-password-input input {

                    padding:
                        0 75px 0 15px;
                }


                .modal-money-input input:focus,
                .modal-password-input input:focus {

                    border-color:
                        var(--primary);

                    box-shadow:
                        0 0 0 4px
                        rgba(93,80,245,.08);
                }


                .password-show {

                    position: absolute;

                    top: 8px;
                    right: 8px;

                    height: 37px;

                    padding:
                        0 11px;

                    border: 0;

                    border-radius: 8px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 11px;

                    font-weight: 800;

                    cursor: pointer;
                }


                .quick-payment-buttons {

                    display: flex;

                    flex-wrap: wrap;

                    gap: 7px;

                    margin-top: 9px;
                }


                .quick-payment-buttons button {

                    padding:
                        7px 10px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 8px;

                    background: #fff;

                    color: var(--text2);

                    font-size: 11px;

                    font-weight: 700;

                    cursor: pointer;
                }


                .quick-payment-buttons button:hover {

                    border-color:
                        var(--primary);

                    color:
                        var(--primary);
                }


                .payment-preview {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding: 15px;

                    margin:
                        17px 0;

                    border-radius: 12px;

                    background:
                        var(--primary-soft);
                }


                .payment-preview span {

                    color: var(--muted);

                    font-size: 13px;

                    font-weight: 600;
                }


                .payment-preview strong {

                    color: var(--primary);

                    font-size: 21px;

                    font-weight: 800;
                }


                .confirm-payment-button {

                    width: 100%;
                    min-height: 53px;

                    border: 0;

                    border-radius: 12px;

                    background:
                        linear-gradient(
                            135deg,
                            var(--primary),
                            var(--primary-dark)
                        );

                    color: #fff;

                    font-size: 15px;

                    font-weight: 800;

                    cursor: pointer;
                }


                .confirm-payment-button:disabled {

                    opacity: .55;

                    cursor: not-allowed;
                }


                /* ==============================
                   HISTORY
                ============================== */

                .history-list {

                    max-height: 420px;

                    overflow-y: auto;
                }


                .history-item {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding: 14px 0;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .history-item-left {

                    display: flex;

                    align-items: center;

                    gap: 11px;
                }


                .history-icon {

                    width: 40px;
                    height: 40px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 11px;

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);

                    font-size: 17px;

                    font-weight: 900;
                }


                .history-item-title {

                    color: var(--text);

                    font-size: 14px;

                    font-weight: 700;
                }


                .history-item small {

                    display: block;

                    margin-top: 3px;

                    color: var(--muted);

                    font-size: 11px;
                }


                .history-amount {

                    color:
                        var(--green-dark);

                    font-size: 15px;

                    font-weight: 800;
                }


                /* ==============================
                   RESPONSIVE
                ============================== */

                @media(max-width:1000px) {

                    .log-summary-grid {

                        grid-template-columns:
                            repeat(2,1fr);
                    }


                    .investment-card-grid {

                        grid-template-columns:
                            1fr;
                    }
                }


                @media(max-width:650px) {

                    .investment-log-page {

                        padding: 12px;
                    }


                    .page-heading {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .page-heading h2 {

                        font-size: 23px;
                    }


                    .log-summary-grid {

                        grid-template-columns:
                            1fr 1fr;

                        gap: 9px;
                    }


                    .log-summary-card {

                        padding: 14px;
                    }


                    .log-summary-card strong {

                        font-size: 18px;
                    }


                    .summary-title {

                        font-size: 12px;
                    }


                    .log-toolbar {

                        flex-direction:
                            column;

                        align-items:
                            stretch;
                    }


                    .log-search {

                        width: 100%;
                    }


                    .investment-card-header {

                        padding: 16px;
                    }


                    .investment-card-body {

                        padding: 16px;
                    }


                    .investment-project-name {

                        font-size: 16px;
                    }


                    .investment-amount-grid {

                        grid-template-columns:
                            1fr;
                    }


                    .investment-amount-box {

                        min-height: auto;
                    }


                    .investment-amount-box span {

                        font-size: 13px;
                    }


                    .investment-amount-box strong {

                        font-size: 20px;
                    }


                    .investment-detail-row span,
                    .investment-detail-row strong {

                        font-size: 13px;
                    }


                    .investment-card-actions {

                        grid-template-columns:
                            1fr;
                    }


                    .modal-amount-summary {

                        grid-template-columns:
                            1fr;
                    }
                }

            `}</style>


            <main className="investment-log-page" 
                style={{
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "20px"
                }}>

                <div className="investment-log-container">


                    {/* ======================================
                        PAGE TITLE
                    ====================================== */}

                    <section className="page-heading">

                        <div>

                            <h2>
                                Investment Log
                            </h2>

                            <p>
                                Track your investment,
                                paid amount, remaining
                                balance and installments.
                            </p>

                        </div>


                        <div className="page-record-count">

                            {income.length}
                            {" "}
                            Investment(s)

                        </div>

                    </section>


                    {/* ======================================
                        SUMMARY
                    ====================================== */}

                    <section className="log-summary-grid">


                        <div className="log-summary-card">

                            <span className="summary-title">
                                Total Target
                            </span>

                            <strong>
                                {money(
                                    summary.target
                                )}
                            </strong>

                        </div>


                        <div className="log-summary-card summary-paid">

                            <span className="summary-title">
                                Total Paid
                            </span>

                            <strong>
                                {money(
                                    summary.paid
                                )}
                            </strong>

                        </div>


                        <div className="log-summary-card summary-remaining">

                            <span className="summary-title">
                                Total Remaining
                            </span>

                            <strong>
                                {money(
                                    summary.remaining
                                )}
                            </strong>

                        </div>


                        <div className="log-summary-card summary-completed">

                            <span className="summary-title">
                                Completed Investment
                            </span>

                            <strong>
                                {summary.completed}
                            </strong>

                        </div>

                    </section>


                    {/* ======================================
                        TOOLBAR
                    ====================================== */}

                    <section className="log-toolbar">

                        <div className="log-toolbar-title">

                            <strong>
                                Investment Records
                            </strong>

                            <span>
                                {filteredLogs.length}
                                {" "}
                                record(s) found
                            </span>

                        </div>


                        <div className="log-search">

                            <span className="log-search-icon">
                                ⌕
                            </span>

                            <input
                                type="text"
                                placeholder="Search investment..."
                                value={search}
                                onChange={(event) => {

                                    setSearch(
                                        event.target.value
                                    );

                                    setCurrentPage(
                                        1
                                    );
                                }}
                            />

                        </div>

                    </section>


                    {/* ======================================
                        CARDS
                    ====================================== */}

                    <section className="investment-card-grid">


                        {loading ? (

                            <Fragment>

                                <div className="investment-skeleton"></div>

                                <div className="investment-skeleton"></div>

                            </Fragment>

                        ) : pageError ? (

                            <div className="log-status-box">

                                <div className="log-status-icon">
                                    !
                                </div>

                                <strong>
                                    Unable to Load
                                </strong>

                                <span>
                                    {pageError}
                                </span>

                            </div>

                        ) : currentLogs.length === 0 ? (

                            <div className="log-status-box">

                                <div className="log-status-icon">
                                    ◈
                                </div>

                                <strong>
                                    No Investment Found
                                </strong>

                                <span>
                                    No investment record
                                    was found.
                                </span>

                            </div>

                        ) : (

                            currentLogs.map(
                                (
                                    item,
                                    index
                                ) => {


                                    /*
                                    ============================================
                                    IMPORTANT

                                    price
                                    = Total Target

                                    booking_amount
                                    = Total Paid
                                    ============================================
                                    */

                                    const target =
                                        Number(
                                            item.target_amount ??
                                            item.share_price ??
                                            item.price ??
                                            0
                                        );


                                    const paid =
                                        Number(
                                            item.paid_amount ??
                                            item.fixt_amount ??
                                            0
                                        );


                                    const remaining =
                                        Math.max(
                                            0,
                                            target -
                                            paid
                                        );


                                    const progress =
                                        target > 0
                                            ? Math.min(
                                                100,
                                                (
                                                    paid /
                                                    target
                                                ) * 100
                                            )
                                            : 0;


                                    const completed =
                                        target > 0 &&
                                        paid >= target;


                                    return (

                                        <article
                                            className="investment-log-card"
                                            key={
                                                item.id ||
                                                item.draft_rand ||
                                                index
                                            }
                                        >


                                            {/* HEADER */}

                                            <div className="investment-card-header">


                                                <div className="investment-project-info">


                                                    <div className="investment-project-icon">
                                                        ◈
                                                    </div>


                                                    <div>

                                                        <div className="investment-project-name">

                                                            {safeText(
                                                                item.packege_name ||
                                                                item.project_name,
                                                                "Investment Project"
                                                            )}

                                                        </div>


                                                        <div className="investment-project-type">

                                                            {safeText(
                                                                item.project_type ||
                                                                item.type,
                                                                "Investment"
                                                            )}

                                                            {" • "}

                                                            ID #

                                                            {item.id ||
                                                                item.draft_rand}

                                                        </div>

                                                    </div>

                                                </div>


                                                <span
                                                    className={
                                                        completed
                                                            ? "payment-status completed"
                                                            : "payment-status"
                                                    }
                                                >

                                                    {completed
                                                        ? "Completed"
                                                        : "In Progress"
                                                    }

                                                </span>

                                            </div>


                                            {/* BODY */}

                                            <div className="investment-card-body">


                                                {/* AMOUNT */}

                                                <div className="investment-amount-grid">


                                                    <div className="investment-amount-box">

                                                        <span>
                                                            Total Target
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                target
                                                            )}
                                                        </strong>

                                                    </div>


                                                    <div className="investment-amount-box paid">

                                                        <span>
                                                            Total Paid
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                paid
                                                            )}
                                                        </strong>

                                                    </div>


                                                    <div className="investment-amount-box remaining">

                                                        <span>
                                                            Remaining
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                remaining
                                                            )}
                                                        </strong>

                                                    </div>

                                                </div>


                                                {/* PROGRESS */}

                                                <div className="progress-section">


                                                    <div className="progress-header">

                                                        <span>
                                                            Investment Progress
                                                        </span>

                                                        <strong>
                                                            {progress.toFixed(
                                                                2
                                                            )}%
                                                        </strong>

                                                    </div>


                                                    <div className="custom-progress">

                                                        <div
                                                            className={
                                                                completed
                                                                    ? "custom-progress-bar complete"
                                                                    : "custom-progress-bar"
                                                            }
                                                            style={{
                                                                width:
                                                                    `${progress}%`
                                                            }}
                                                        ></div>

                                                    </div>


                                                    <div className="progress-numbers">

                                                        <span>
                                                            {money(
                                                                paid
                                                            )}
                                                            {" "}
                                                            paid
                                                        </span>

                                                        <span>
                                                            {money(
                                                                target
                                                            )}
                                                            {" "}
                                                            target
                                                        </span>

                                                    </div>

                                                </div>


                                                {/* DETAILS */}

                                                <div className="investment-detail-row">

                                                    <span>
                                                        Total Target
                                                    </span>

                                                    <strong>
                                                        {money(
                                                            target
                                                        )}
                                                    </strong>

                                                </div>


                                                <div className="investment-detail-row">

                                                    <span>
                                                        Total Paid
                                                    </span>

                                                    <strong>
                                                        {money(
                                                            paid
                                                        )}
                                                    </strong>

                                                </div>


                                                <div className="investment-detail-row">

                                                    <span>
                                                        Remaining Balance
                                                    </span>

                                                    <strong>
                                                        {money(
                                                            remaining
                                                        )}
                                                    </strong>

                                                </div>


                                                <div className="investment-detail-row">
                                                    <span>Initial Payment</span>
                                                    <strong>{money(item.fixt_amount)}</strong>
                                                </div>

                                                <div className="investment-detail-row">
                                                    <span>Total Return</span>
                                                    <strong>{money(item.already_back_amount)}</strong>
                                                </div>

                                                <div className="investment-detail-row">
                                                    <span>Package Type</span>
                                                    <strong>{safeText(item.pack_type || item.type)}</strong>
                                                </div>

                                                <div className="investment-detail-row">
                                                    <span>Minimum Booking</span>
                                                    <strong>{money(item.minimum_booking_amount)}</strong>
                                                </div>

                                                <div className="investment-detail-row">

                                                    <span>
                                                        Investment Date
                                                    </span>

                                                    <strong>
                                                        {formatDate(
                                                            item.create_date
                                                        )}
                                                    </strong>

                                                </div>


                                                <div className="investment-detail-row">

                                                    <span>
                                                        Status
                                                    </span>

                                                    <strong>

                                                        {completed
                                                            ? "Investment Completed"
                                                            : "Installment Running"
                                                        }

                                                    </strong>

                                                </div>


                                                {/* BUTTON */}

                                                <div className="investment-card-actions">



                                                    <button
                                                        type="button"
                                                        className="installment-button"
                                                        disabled={
                                                            completed
                                                        }
                                                        onClick={() =>
                                                            openPaymentModal(
                                                                item
                                                            )
                                                        }
                                                    >

                                                        {completed
                                                            ? "✓ Investment Completed"
                                                            : "+ Pay Installment"
                                                        }

                                                    </button>

                                                </div>

                                            </div>

                                        </article>
                                    );
                                }
                            )
                        )}

                    </section>


                    {/* ======================================
                        PAGINATION
                    ====================================== */}

                    {!loading &&
                        filteredLogs.length >
                        PER_PAGE && (

                        <div className="log-pagination">


                            <button
                                type="button"
                                className="log-page-button"
                                disabled={
                                    currentPage === 1
                                }
                                onClick={() =>
                                    changePage(
                                        currentPage - 1
                                    )
                                }
                            >
                                ‹
                            </button>


                            {Array.from(
                                {
                                    length:
                                        totalPages
                                },
                                (
                                    _,
                                    index
                                ) =>
                                    index + 1
                            ).map(
                                (page) => (

                                    <button
                                        type="button"
                                        key={page}
                                        className={
                                            currentPage ===
                                            page
                                                ? "log-page-button active"
                                                : "log-page-button"
                                        }
                                        onClick={() =>
                                            changePage(
                                                page
                                            )
                                        }
                                    >
                                        {page}
                                    </button>

                                )
                            )}


                            <button
                                type="button"
                                className="log-page-button"
                                disabled={
                                    currentPage ===
                                    totalPages
                                }
                                onClick={() =>
                                    changePage(
                                        currentPage + 1
                                    )
                                }
                            >
                                ›
                            </button>

                        </div>
                    )}

                </div>

            </main>


            {/* ==========================================
                PAYMENT MODAL
            ========================================== */}

            <Modal
                show={
                    showPaymentModal
                }
                onHide={
                    closePaymentModal
                }
                centered
                className="installment-modal"
            >

                <form
                    onSubmit={
                        submitInstallment
                    }
                >


                    <div className="installment-modal-header">

                        <h4>
                            Pay Installment
                        </h4>

                        <p>
                            Add an installment payment
                            to this investment.
                        </p>


                        <button
                            type="button"
                            className="modal-close-button"
                            onClick={
                                closePaymentModal
                            }
                        >
                            ×
                        </button>

                    </div>


                    <div className="installment-modal-body">


                        <div className="modal-project-box">

                            <small>
                                Selected Investment
                            </small>

                            <strong>

                                {safeText(
                                    selectedLog
                                        ?.packege_name ||
                                    selectedLog
                                        ?.project_name,
                                    "Investment Project"
                                )}

                            </strong>

                        </div>


                        {/* SUMMARY */}

                        <div className="modal-amount-summary">


                            <div>

                                <span>
                                    Total Target
                                </span>

                                <strong>
                                    {money(
                                        selectedLog
                                            ?.target_amount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    Total Paid
                                </span>

                                <strong>
                                    {money(
                                        selectedLog
                                            ?.paid_amount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    Remaining
                                </span>

                                <strong>
                                    {money(
                                        selectedLog
                                            ?.remaining_amount
                                    )}
                                </strong>

                            </div>

                        </div>


                        {/* AMOUNT */}

                        <div className="modal-field">

                            <label>
                                Installment Amount
                            </label>


                            <div className="modal-money-input">

                                <span>
                                    ৳
                                </span>

                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="Enter installment amount"
                                    value={
                                        paymentAmount
                                    }
                                    onChange={
                                        handleAmountChange
                                    }
                                    autoFocus
                                />

                            </div>


                            <div className="quick-payment-buttons">


                                <button
                                    type="button"
                                    onClick={() =>
                                        setQuickPayment(
                                            5000
                                        )
                                    }
                                >
                                    ৳5,000
                                </button>


                                <button
                                    type="button"
                                    onClick={() =>
                                        setQuickPayment(
                                            10000
                                        )
                                    }
                                >
                                    ৳10,000
                                </button>


                                <button
                                    type="button"
                                    onClick={() =>
                                        setQuickPayment(
                                            50000
                                        )
                                    }
                                >
                                    ৳50,000
                                </button>


                                <button
                                    type="button"
                                    onClick={() =>
                                        setQuickPayment(
                                            selectedLog
                                                ?.remaining_amount
                                        )
                                    }
                                >
                                    Pay Full Remaining
                                </button>

                            </div>

                        </div>


                        {/* PASSWORD */}

                        <div className="modal-field">

                            <label>
                                Account Password
                            </label>


                            <div className="modal-password-input">

                                <input
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    placeholder="Enter account password"
                                    value={
                                        password
                                    }
                                    onChange={(event) =>
                                        setPassword(
                                            event.target.value
                                        )
                                    }
                                />


                                <button
                                    type="button"
                                    className="password-show"
                                    onClick={() =>
                                        setShowPassword(
                                            (value) =>
                                                !value
                                        )
                                    }
                                >

                                    {showPassword
                                        ? "Hide"
                                        : "Show"
                                    }

                                </button>

                            </div>

                        </div>


                        {/* PAYMENT PREVIEW */}

                        <div className="payment-preview">

                            <span>
                                Payment Amount
                            </span>

                            <strong>
                                {money(
                                    paymentAmount
                                )}
                            </strong>

                        </div>


                        <button
                            type="submit"
                            className="confirm-payment-button"
                            disabled={
                                paymentLoading ||
                                !Number(
                                    paymentAmount
                                ) ||
                                !password.trim()
                            }
                        >

                            {paymentLoading
                                ? "Processing Payment..."
                                : `Pay ${money(
                                    paymentAmount
                                )}`
                            }

                        </button>

                    </div>

                </form>

            </Modal>

        </Fragment>
    );
};


export default BasicDatatable;